import { createReducer } from '@reduxjs/toolkit'
import { get, set, forEach, includes, without } from 'lodash'

// the initial state under a pagination 'slice'
// e.g. for a single page
const initialState = {
  isFetching: false,
  hasFetchedBefore: false,

  ids: [],

  currentPage: 1,
  limitValue: undefined,
  totalCount: undefined,
}

const defaultMapActionToKey = action => action.payload.pageNumber.toString()
const defaultMapActionToPaginationTotalsKey = () => 'lastTotals'

// Creates a reducer managing pagination, given the action types to handle,
// and a function telling how to extract the key from an action.
export function paginate({
  types,
  mapActionToKey = defaultMapActionToKey,
  mapActionToTotalsKey = defaultMapActionToPaginationTotalsKey,
}) {
  if (!Array.isArray(types)) {
    throw new Error('Expected types to be an array')
  }
  if (!types.every(t => typeof t === 'string')) {
    throw new Error('Expected types to be strings.')
  }
  if (typeof mapActionToKey !== 'function') {
    throw new Error('Expected mapActionToKey to be a function.')
  }

  const [requestType, successType, failureType, removalType] = types

  const updatePagination = (state, action) => {
    // perhaps nothing under this key yet, so begin with something
    if (!state) {
      state = {
        ...initialState,
      }
    }

    // helper functions
    const paginateRequest = function (state, action) {
      state.isFetching = true
      return state
    }
    const paginateSuccess = function (state, action) {
      const {
        payload: { pagination, ids },
      } = action
      Object.assign(state, {
        isFetching: false,
        hasFetchedBefore: true,
        ...pagination,
        ids,
      })
      return state
    }
    const paginateFail = function (state, action) {
      state.isFetching = false
      return state
    }
    // removes a single item from paginables, e.g. the id of a deleted record
    const paginateRemove = function (state, action) {
      const { payload } = action
      const idToRemove = payload
      const stateKeys = Object.keys(state)
      forEach(stateKeys, key => {
        // Note: We may be running this over the pagination root node, so can't assume we have an ids property
        const existingIds = get(state, [key, 'ids'])
        if (existingIds && includes(existingIds, idToRemove)) {
          set(state, [key, 'ids'], without(existingIds, idToRemove))
        }
      })
      return state
    }

    const { type } = action
    switch (type) {
      case requestType:
        return paginateRequest(state, action)
      case successType:
        return paginateSuccess(state, action)
      case failureType:
        return paginateFail(state, action)
      case removalType:
        return paginateRemove(state, action)
    }
  }

  // The 'main' pagination function that gets called for all action types that match
  const updatePaginationByKey = (state, action) => {
    const { payload, type } = action

    // first get the key under which the pagination is stored
    const key = mapActionToKey(action)
    if (typeof key !== 'string') {
      throw new Error('Expected key to be a string.')
    }

    // then get the key under which the 'totals' are kept
    const totalsKey = mapActionToTotalsKey(action)
    let totals = state[totalsKey] || {}
    if (payload.hasOwnProperty('pagination')) {
      const { pagination } = payload
      totals = {
        ...totals,
        ...pagination,
      }
    }

    // now do the actual updating of the state slice
    state[totalsKey] = totals
    if (key === '') {
      // run over the root, we're removing an individual id from a pagination page's ids array
      updatePagination(state, action)
    } else {
      // overwrite a sub-slice
      state[key] = updatePagination(state[key], action)
    }

    return
  }

  return createReducer(initialState, builder => {
    builder.addMatcher(
      ({ type }) => includes([requestType, successType, failureType, removalType], type),
      (state, action) => updatePaginationByKey(state, action)
    )
  })
}
